import { CourseAnswerComparisons } from './course-answer-comparisons';
import { hasDuplicateComparisonPairs } from '../../general/course-form-comparisons';
import { TabView, TabPanel } from "primereact/tabview";
import { Button } from "primereact/button";
import { CourseEditorGeneralTab } from "./course-edit-general";
import { CourseEditorModulesTab } from "./course-edit-modules";
import { useCallback, useContext, useEffect, useState } from "react";
import { CourseManagementDataContext } from "../../providers/course-management-provider";
import { CourseEditorUsersTab } from "./course-edit-users";
import { CoursePromos } from "./course-promos";
import { CourseLoyalty } from './course-loyalty';
import { DiscountDraftAction } from '../../general/course-discount-draft';
import { CoursePromoDto, CourseLoyaltyDto, CourseDto } from '../../general/dtos';
import { usePendingEdit } from '../../general/unsaved-changes-context';

const newCourseBaseline: Partial<CourseDto> = { name: 'New Course', description: '', price: null,
    certificate_enabled: true, cert_description: null, cert_prefix: null, course_prerequisites: [],
    modules: [], users: [], promotions: [], loyalty_discounts: [], answer_comparisons: [] };

export function CourseEditorTabs() {
    const ctx = useContext(CourseManagementDataContext)!;
    const { cancelEditing, saveCourse, registerCourseDraft, mode, selectedCourseFull: course } = ctx;
    const courseKey = course?.course_key ?? '';
    const [activeTab, setActiveTab] = useState(0);
    const [promotions, setPromotions] = useState<{ key: string; action: DiscountDraftAction<CoursePromoDto> | null } | null>(null);
    const [loyalty, setLoyalty] = useState<{ key: string; action: DiscountDraftAction<CourseLoyaltyDto> | null } | null>(null);
    const [sectionError, setSectionError] = useState('');
    const registerPromotions = useCallback((action: DiscountDraftAction<CoursePromoDto> | null) => setPromotions({ key: courseKey, action }), [courseKey]);
    const registerLoyalty = useCallback((action: DiscountDraftAction<CourseLoyaltyDto> | null) => setLoyalty({ key: courseKey, action }), [courseKey]);
    useEffect(() => { setActiveTab(0); setSectionError(''); }, [courseKey, mode]);
    const promoForm = promotions?.key === courseKey ? promotions.action : null;
    const loyaltyForm = loyalty?.key === courseKey ? loyalty.action : null;
    const collect = useCallback((): Partial<CourseDto> | null => {
        setSectionError('');
        if (hasDuplicateComparisonPairs(course?.answer_comparisons ?? [])) {
            setActiveTab(5); setSectionError('These two fragments already have a comparison. Edit or delete the duplicate comparison before saving.'); return null;
        }
        const promos = promoForm?.collect() ?? (promoForm ? null : course?.promotions ?? []);
        if (promos === null) { setActiveTab(3); setSectionError('Review the promotion form. Nothing has been saved.'); return null; }
        const rules = loyaltyForm?.collect() ?? (loyaltyForm ? null : course?.loyalty_discounts ?? []);
        if (rules === null) { setActiveTab(4); setSectionError('Review the loyalty discount form. Nothing has been saved.'); return null; }
        return { promotions: promos, loyalty_discounts: rules };
    }, [promoForm, loyaltyForm, course]);
    useEffect(() => registerCourseDraft(collect), [registerCourseDraft, collect]);
    usePendingEdit({ dirty: () => !!promoForm?.dirty || !!loyaltyForm?.dirty, flush: () => {},
        cancel: () => { promoForm?.discard(); loyaltyForm?.discard(); } });
    useEffect(() => {
        const problem = ctx.saveErrorLocation;
        if (!problem) return;
        const tab = { general: 0, modules: 1, users: 2, promotions: 3, loyalty_discounts: 4, answer_comparisons: 5 }[problem.section] ?? 0;
        setActiveTab(tab);
        if (!problem.field) return;
        const frame = requestAnimationFrame(() => document.getElementById(problem.field!)?.focus());
        return () => cancelAnimationFrame(frame);
    }, [ctx.saveErrorLocation]);
    const original = mode === 'create' ? newCourseBaseline : ctx.originalCourseFull;
    const changed = (fields: (keyof CourseDto)[]) => fields.some(field => JSON.stringify(course?.[field]) !== JSON.stringify(original?.[field]));
    const dirtyTabs = [changed(['name', 'description', 'price', 'certificate_enabled', 'cert_description', 'cert_prefix', 'course_prerequisites']),
        changed(['modules']), changed(['users']), changed(['promotions']) || !!promoForm?.dirty, changed(['loyalty_discounts']) || !!loyaltyForm?.dirty, changed(['answer_comparisons'])];
    const header = (title: string, index: number) => <span title={dirtyTabs[index] ? 'Unsaved changes' : undefined}>{title}
        {dirtyTabs[index] && <span aria-hidden="true" className="text-primary"> {'\u2022'}</span>}</span>;
    const busy = ctx.selectedCourseLoading;
    if (!course) return <p>No course selected.</p>;
    return <>
        <div className="course-editor flex flex-column">
            <div className="course-editor-main flex flex-column" inert={busy}>
                <TabView className="course-editor-tabs flex flex-column" activeIndex={activeTab} renderActiveOnly={false}
                    onTabChange={event => setActiveTab(event.index)}>
                    <TabPanel header={header('General', 0)}><div className="editor-tab-content p-3"><CourseEditorGeneralTab /></div></TabPanel>
                    <TabPanel header={header('Modules & dependencies', 1)}><div className="editor-tab-content p-3"><CourseEditorModulesTab /></div></TabPanel>
                    <TabPanel header={header('Users', 2)}><div className="editor-tab-content p-3"><CourseEditorUsersTab /></div></TabPanel>
                    <TabPanel header={header('Promotions', 3)}><div className="editor-tab-content p-3">
                        <CoursePromos key={courseKey} promos={course.promotions ?? []} busy={busy}
                            onChange={value => ctx.updateCourseField('promotions', value)} onDraftChange={registerPromotions} />
                    </div></TabPanel>
                    <TabPanel header={header('Loyalty discounts', 4)}><div className="editor-tab-content p-3">
                        <CourseLoyalty key={courseKey} courseKey={courseKey} courseName={course.name} courses={ctx.allCourses}
                            rules={course.loyalty_discounts ?? []} busy={busy}
                            onChange={value => ctx.updateCourseField('loyalty_discounts', value)} onDraftChange={registerLoyalty} />
                    </div></TabPanel>
                    <TabPanel header={header('Answer comparisons', 5)}><div className="editor-tab-content p-3">
                        <CourseAnswerComparisons course={course} modules={ctx.allModules} disabled={busy}
                            onChange={value => ctx.updateCourseField('answer_comparisons', value)} />
                    </div></TabPanel>
                </TabView>
            </div>
            {sectionError && <p role="alert" className="px-3">{sectionError}</p>}
            <div className="admin-toolbar p-3 border-top-1 surface-border flex justify-content-end gap-2">
                <Button label="Cancel" icon="pi pi-times" className="p-button-text" disabled={busy}
                    onClick={() => { promoForm?.discard(); loyaltyForm?.discard(); cancelEditing(); }} />
                <Button label="Save" icon="pi pi-check" loading={busy} disabled={busy} onClick={saveCourse} />
            </div>
        </div>
    </>;
}
